import { useDispatch, useSelector } from 'react-redux';
import { setFormField, setFieldError, fetchInitialState, UploadBranchImages, DeleteBranchImages, updateDetailsOption, updateDBAOption, updateWebsiteOption, updateSocialMediaProfilesOption, updateTeamPhotoOption, BRANCH_PHOTO_LOCATION_OPTIONS, hasSocialMediaProfile } from './marketingDetailsSlice';
import FloatingLabelInput from "../../components/floatingLabelInput";

import { formatPhoneNumber, getFileName, toFileList } from '../utils';
import { useEffect } from 'react';
import Loader from '../../components/loader';
import { OptionCard, OptionCardRow, StepHeading, StepShell } from '../../components/optionCard';
import { BACKEND_API_URL } from '../../config';

import { fetchInitialState as fetchBranchLocationData } from '../4_5_6_BranchLocationDetails/branchLocationDetailsSlice';

const SOCIAL_MEDIA_FIELDS = [
    { name: 'branchLinkedIn', label: 'Branch LinkedIn' },
    { name: 'branchTwitterXProfile', label: 'Branch Twitter(X) Profile' },
    { name: 'branchFacebookProfile', label: 'Branch Facebook Profile' },
    { name: 'branchInstagramProfile', label: 'Branch Instagram Profile' },
    { name: 'branchZillowProfile', label: 'Branch Zillow Profile' },
    { name: 'branchGoogleMyBusiness', label: 'Branch Google My Business' },
];

// Text fields that must not be left empty, with the message shown when they are.
const REQUIRED_TEXT_FIELDS = {
    companyBranchName: 'Please enter the team / branch name.',
};

const isWebsiteValid = (value) => value.endsWith('.com') || value.endsWith('.biz');

// One question: centred heading, optional hint, and a red * when mandatory.
const Question = ({ children, hint, required = false, className = 'mt-14' }) => (
    <div className={className}>
        <h3 className="break-words text-center text-xl font-bold text-[#0a1445] sm:text-2xl">
            {children}
            {required && <span className="text-red-600" aria-hidden="true"> *</span>}
        </h3>
        {hint && <p className="mt-1 text-center text-sm text-slate-500 sm:text-base">{hint}</p>}
    </div>
);

// Inline "this still needs an answer" message, shown after Next fails.
const MissingAnswer = ({ show, children }) =>
    show ? (
        <p role="alert" className="mt-3 text-center text-sm font-medium text-red-600">
            {children}
        </p>
    ) : null;

// Every single input / upload box shares this width so the form lines up.
const FieldColumn = ({ children, className = 'mt-10' }) => (
    <div className={`mx-auto w-full max-w-md ${className}`}>{children}</div>
);

const stopDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
};

const UploadZone = ({ id, title, required = false, onFiles }) => (
    <FieldColumn>
        <label
            htmlFor={id}
            onDragOver={stopDrag}
            onDragEnter={stopDrag}
            onDragLeave={stopDrag}
            onDrop={(e) => {
                stopDrag(e);
                onFiles(e);
            }}
            className="flex w-full cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 pt-5 pb-6 text-center transition-colors hover:border-[#fb6635] hover:bg-orange-50"
        >
            <svg className="mb-4 h-8 w-8 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
            </svg>
            <p className="mb-2 text-sm text-gray-500">
                <span className="font-semibold">{title}</span>
                {required && <span className="text-red-600"> *</span>}
            </p>
            <p className="mb-2 text-sm text-gray-500">or drag and drop</p>
            <input
                name={id}
                id={id}
                onChange={onFiles}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
            />
        </label>
    </FieldColumn>
);

// Uploaded files, aligned with the upload box above them.
const UploadedFiles = ({ files, onDownload, onDelete }) => {
    const list = toFileList(files);
    if (list.length === 0) return null;
    return (
        <FieldColumn className="mt-4 space-y-2">
            {list.map((image, index) => (
                <div key={index} className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <span className="min-w-0 truncate text-sm text-gray-700" title={getFileName(image)}>
                        {getFileName(image)}
                    </span>
                    <div className="flex shrink-0 items-center gap-1">
                        <button
                            type="button"
                            onClick={(e) => onDownload(e, image)}
                            className="rounded px-2 py-1 text-sm text-slate-600 hover:bg-slate-100"
                            title="Download"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="20" fill="currentColor">
                                <path d="M0 0h24v24H0z" fill="none"/>
                                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                            </svg>
                        </button>
                        <button
                            type="button"
                            onClick={(e) => onDelete(e, image)}
                            className="rounded px-2 py-1 text-sm text-red-600 hover:bg-red-50"
                            title="Delete"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-trash" viewBox="0 0 16 16">
                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/>
                                <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/>
                            </svg>
                        </button>
                    </div>
                </div>
            ))}
        </FieldColumn>
    );
};

const MarketingDetails = () => {
    const dispatch = useDispatch();
    const marketingDetailsState = useSelector(state => state.marketing);
    const branchLocationState = useSelector(state => state.branch_location);
    const linkIsDBA = useSelector(state => state.navigator.linkMeta?.isDBA);
    const data = marketingDetailsState.data;
    const formErrors = marketingDetailsState.formErrors;

    /**
     * The formal DBA question is answered by the hiring manager while generating the link:
     *  - "Yes" -> the DBA section is shown here
     *  - "No"  -> the DBA section is hidden and stored as No
     *  - null  -> the link has no answer (older links), so keep asking here as before
     */
    const showDbaSection = linkIsDBA == null ? true : linkIsDBA === 'Yes';
    // Only ask for a Branch Photo if they answered the previous slide that they had a location
    const showBranchPhoto = BRANCH_PHOTO_LOCATION_OPTIONS.includes(branchLocationState?.data?._option);

    // Mirrors the checks SubmitMarketingDetails runs on Next; once a Next attempt
    // has failed, every question still missing an answer is flagged inline.
    const showErrors = marketingDetailsState.submitAttempted;
    const isAnswered = (value) => value === 'Yes' || value === 'No';
    const missing = {
        website: !isAnswered(data.isWebsite),
        socialMedia: !isAnswered(data.isSocialMediaProfiles),
        socialProfile: data.isSocialMediaProfiles === 'Yes' && !hasSocialMediaProfile(data),
        teamPhoto: !isAnswered(data.isTeamPhoto),
        teamPhotoFiles: data.isTeamPhoto === 'Yes' && toFileList(data.teamPhotos).length === 0,
        branchPhotoFiles: showBranchPhoto && toFileList(data.branchPhotos).length === 0,
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                await Promise.all([
                    dispatch(fetchInitialState()),
                    dispatch(fetchBranchLocationData())
                ]);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, [dispatch]);

    // The team / branch name is now mandatory for every flow, so the old Yes/No answer
    // is always "Yes"
    useEffect(() => {
        if (data.isDetails !== 'Yes') {
            dispatch(updateDetailsOption('Yes'));
        }
    }, [dispatch, data.isDetails]);

    useEffect(() => {
        if (!showDbaSection && data.isDBA !== 'No') {
            dispatch(updateDBAOption('No'));
        }
    }, [dispatch, showDbaSection, data.isDBA]);

    // A failed Next leaves the team-name box untouched, so flag it here too.
    useEffect(() => {
        if (showErrors && !data.companyBranchName?.trim()) {
            dispatch(setFieldError({ field: 'companyBranchName', error: REQUIRED_TEXT_FIELDS.companyBranchName }));
        }
    }, [dispatch, showErrors, data.companyBranchName]);

    const handleFileChange = (e, label) => {
        const files = Array.from(e.target.files || e.dataTransfer.files);
        dispatch(UploadBranchImages({'data': data, 'files': files, 'label': label}));
        e.target.value = null;
    };

    const handleFileDownload = (e, image) => {
        e.preventDefault()
        const url = `${BACKEND_API_URL}/api/files/download?path=${encodeURIComponent(image)}`;
          fetch(url)
            .then(response => response.blob())
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                // Specify a filename for the downloaded file
                a.download = getFileName(image) || 'download';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
            })
            .catch(err => console.error('Fetch error:', err));
      };

    const handleFileDelete = (e, image, label) => {
        e.preventDefault();
        dispatch(DeleteBranchImages({ imageToDelete: image, label }));
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'branchPhoneNumber') {
            dispatch(setFormField({ field: name, value: formatPhoneNumber(value) }));
            dispatch(setFieldError({ field: name, error: ''}));
        } else if (name === 'branchWebsite') {
            dispatch(setFormField({ field: name, value }));
            dispatch(setFieldError({ field: name, error: isWebsiteValid(value) ? '' : 'Branch website must end with .com or .biz' }));
        } else {
            dispatch(setFormField({ field: name, value }));
            // Only mandatory fields complain when emptied; the social profiles are optional.
            const requiredMessage = REQUIRED_TEXT_FIELDS[name];
            dispatch(setFieldError({ field: name, error: requiredMessage && !value.trim() ? requiredMessage : '' }));
        }
    }

    const handleBlur = (e) => {
        const { name, value } = e.target;
        if (name === 'branchPhoneNumber') {
            // Check if the number has 10 digits
            const rawNumbers = value.replace(/[^\d]/g, '');
            if (rawNumbers.length !== 10) {
                dispatch(setFieldError({ field: name, error: "Please enter a valid phone number" }));
            }
        } else if (name === 'branchWebsite') {
            if (!value.trim()) {
                dispatch(setFieldError({ field: name, error: 'Please enter the branch website.' }));
            } else if (!isWebsiteValid(value)) {
                dispatch(setFieldError({ field: name, error: 'Branch website must end with .com or .biz' }));
            }
        } else if (REQUIRED_TEXT_FIELDS[name] && !value.trim()) {
            dispatch(setFieldError({ field: name, error: REQUIRED_TEXT_FIELDS[name] }));
        }
      };

    return (
        <>
        {marketingDetailsState.loading && <Loader />}
            <StepShell>
                <StepHeading>
                    Marketing Setup
                </StepHeading>
            </StepShell>
            <div className='mt-2 flex flex-col items-center justify-center px-1'>
                <p className="text-center text-sm text-slate-500 sm:text-base">(Please answer below key questions, So our marketing team can get started)</p>
                <p className="mt-1 text-center text-xs text-slate-400 sm:text-sm">
                    Questions marked with <span className="text-red-600">*</span> are required.
                </p>
            </div>
            <div className="mx-auto mt-10 w-full max-w-3xl px-1 sm:mt-14">
                {/* Navigation's Next button saves this step; pressing Enter in a
                    field must not submit (and reload) the page. */}
                <form onSubmit={(e) => e.preventDefault()}>
                    {/* Team / Branch name — mandatory for every onboarding flow */}
                    <Question required className="" hint="(a team/branch name is required)">
                        What is the team name, location or nickname for your branch/team?
                    </Question>
                    <FieldColumn>
                        <FloatingLabelInput
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            value={data.companyBranchName}
                            error={formErrors.companyBranchName}
                            name="companyBranchName"
                            id="companyBranchName"
                            label="Team / Branch Name"
                            required={true}
                        />
                    </FieldColumn>

                    {/* Website Information */}
                    <Question required hint="(this is not for individual websites only the branch/team)">
                        Do you have an existing website that you use for your branch/team?
                    </Question>
                    <OptionCardRow>
                        <OptionCard
                            selected={data.isWebsite === 'Yes'}
                            onClick={() => dispatch(updateWebsiteOption('Yes'))}
                        >
                            Yes
                        </OptionCard>
                        <OptionCard
                            selected={data.isWebsite === 'No'}
                            onClick={() => dispatch(updateWebsiteOption('No'))}
                        >
                            No
                        </OptionCard>
                    </OptionCardRow>
                    <MissingAnswer show={showErrors && missing.website}>Please select Yes or No.</MissingAnswer>

                    {data.isWebsite === 'Yes' && (
                        <FieldColumn>
                            <FloatingLabelInput
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                value={data.branchWebsite}
                                error={formErrors.branchWebsite || (showErrors && !data.branchWebsite ? 'Please enter the branch website.' : null)}
                                name="branchWebsite"
                                id="branchWebsite"
                                type="text"
                                label="Branch Website"
                                required={true}
                            />
                        </FieldColumn>
                    )}

                    {/* Social Media Profiles Information */}
                    <Question required hint="(this is not for individual profiles only the branch/team)">
                        Do you have any existing social media profiles for your branch/team?
                    </Question>
                    <OptionCardRow>
                        <OptionCard
                            selected={data.isSocialMediaProfiles === 'Yes'}
                            onClick={() => dispatch(updateSocialMediaProfilesOption('Yes'))}
                        >
                            Yes
                        </OptionCard>
                        <OptionCard
                            selected={data.isSocialMediaProfiles === 'No'}
                            onClick={() => dispatch(updateSocialMediaProfilesOption('No'))}
                        >
                            No
                        </OptionCard>
                    </OptionCardRow>
                    <MissingAnswer show={showErrors && missing.socialMedia}>Please select Yes or No.</MissingAnswer>

                    {data.isSocialMediaProfiles === 'Yes' && (
                        <>
                            <p className="mt-10 text-center text-sm text-slate-500">
                                Add at least one profile <span className="text-red-600">*</span>
                            </p>
                            <div className="mx-auto mt-6 grid w-full max-w-2xl grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                                {SOCIAL_MEDIA_FIELDS.map(({ name, label }) => (
                                    <FloatingLabelInput
                                        key={name}
                                        handleChange={handleChange}
                                        value={data[name]}
                                        error={formErrors[name]}
                                        name={name}
                                        id={name}
                                        type="text"
                                        label={label}
                                    />
                                ))}
                            </div>
                            <MissingAnswer show={showErrors && missing.socialProfile}>
                                Please add at least one social media profile.
                            </MissingAnswer>
                        </>
                    )}

                    {/* Branch or Team photos */}
                    <Question required>
                        Do you have any branch or team photos you would like our marketing department to use for your team?
                    </Question>
                    <OptionCardRow>
                        <OptionCard
                            selected={data.isTeamPhoto === 'Yes'}
                            onClick={() => dispatch(updateTeamPhotoOption('Yes'))}
                        >
                            Yes
                        </OptionCard>
                        <OptionCard
                            selected={data.isTeamPhoto === 'No'}
                            onClick={() => dispatch(updateTeamPhotoOption('No'))}
                        >
                            No
                        </OptionCard>
                    </OptionCardRow>
                    <MissingAnswer show={showErrors && missing.teamPhoto}>Please select Yes or No.</MissingAnswer>

                    {data.isTeamPhoto === "Yes" && (
                        <>
                            <UploadZone
                                id="teamphotos"
                                title="Click to upload Branch/Team Photos"
                                required
                                onFiles={(e) => handleFileChange(e, 'teamPhotos')}
                            />
                            <MissingAnswer show={showErrors && missing.teamPhotoFiles}>
                                Please upload at least one branch or team photo.
                            </MissingAnswer>
                        </>
                    )}
                    <UploadedFiles
                        files={data.teamPhotos}
                        onDownload={handleFileDownload}
                        onDelete={(e, image) => handleFileDelete(e, image, 'teamPhoto')}
                    />

                    {showDbaSection && (<>
                        <Question>
                            Will this opportunity be a formal DBA?
                        </Question>
                        <OptionCardRow>
                            <OptionCard
                                selected={data.isDBA === 'Yes'}
                                onClick={() => dispatch(updateDBAOption('Yes'))}
                            >
                                Yes
                            </OptionCard>
                            <OptionCard
                                selected={data.isDBA === 'No'}
                                onClick={() => dispatch(updateDBAOption('No'))}
                            >
                                No
                            </OptionCard>
                        </OptionCardRow>
                    </>)}

                    {/* Photos Gallery */}
                    {showBranchPhoto && (
                        <>
                            <Question required hint="(Please upload a branch photo to be displayed on Google my business)">
                                Branch Photo
                            </Question>
                            <UploadZone
                                id="photos"
                                title="Click to upload Branch Photos"
                                required
                                onFiles={(e) => handleFileChange(e, 'branchPhotos')}
                            />
                            <MissingAnswer show={showErrors && missing.branchPhotoFiles}>
                                Please upload a branch photo.
                            </MissingAnswer>
                            <UploadedFiles
                                files={data.branchPhotos}
                                onDownload={handleFileDownload}
                                onDelete={(e, image) => handleFileDelete(e, image, 'branchPhoto')}
                            />
                        </>
                    )}

                    {/* Image Gallery */}
                    <Question>
                        Please upload additional branch images as needed (Optional)
                    </Question>
                    <UploadZone
                        id="images"
                        title="Click to upload Branch Images"
                        onFiles={(e) => handleFileChange(e, 'branchImages')}
                    />
                    <div className="mb-8">
                        <UploadedFiles
                            files={data.branchImages}
                            onDownload={handleFileDownload}
                            onDelete={(e, image) => handleFileDelete(e, image, 'branchImage')}
                        />
                    </div>
                </form>
            </div>
        </>
    )
}

export default MarketingDetails;
